import React, { useEffect, useState } from 'react';
import { Bot, MessageSquareText, Shield, BarChart2, ArrowRight, Sparkles, Mail, CheckCircle, AlertCircle } from 'lucide-react';

interface HubspotFormProps {
  onClose?: () => void;
}

type Status = 'idle' | 'sending' | 'success' | 'error';

function HubspotForm({ onClose }: HubspotFormProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [email, setEmail] = useState('');

  const handleSubmit = async () => {
    if (!email) {
      setStatus('error');
      return;
    }

    setStatus('sending');
    try {
      const contextMessage = `I'm interested in joining the beta program for Agent Mojo.
Current page: Homepage
Feature requested: Beta Access
User email: ${email}`;

      const response = await fetch('https://api.hsforms.com/submissions/v3/integration/submit/47155561/a6efc3de-4530-412e-8802-25594a1a8c95', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fields: [
            {
              name: "email",
              value: email
            },
            {
              name: "message",
              value: contextMessage
            }
          ],
          context: {
            pageUri: window.location.href,
            pageName: "Homepage",
            hutk: document.cookie.match(/hubspotutk=(.*?);/)?.[1]
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setStatus('success');
    } catch (error) {
      console.error('Failed to submit form:', error);
      setStatus('error');
      // Fallback to mailto
      const mailtoBody = `Hi Agent Mojo team,

I'm interested in joining the beta program.

Best regards,
${email}`;

      window.open(`mailto:sales@agentmojo.ai?subject=Interested%20in%20Beta%20Access&body=${encodeURIComponent(mailtoBody)}`, '_blank');
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'success':
        return (
          <>
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Thank You!</h2>
            <p className="text-gray-600 mb-6">
              We'll contact you shortly with information about our beta program.
            </p>
          </>
        );
      case 'error':
        return (
          <>
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Oops!</h2>
            <p className="text-gray-600 mb-6">
              Something went wrong. Please try again or use the email option.
            </p>
            <button 
              onClick={() => setStatus('idle')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-all duration-200 font-medium"
            >
              Try Again
            </button>
          </>
        );
      default:
        return (
          <>
            <Mail className="w-12 h-12 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Join Our Beta Program</h2>
            <p className="text-gray-600 mb-6">
              Be among the first to experience AI SDR quality assurance and monitoring.
            </p>
            <div className="mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <button 
              onClick={handleSubmit}
              disabled={status === 'sending'}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed w-full justify-center"
            >
              {status === 'sending' ? 'Sending...' : 'Join Beta'}
            </button>
          </>
        );
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center">
        {renderContent()}
      </div>
    </div>
  );
}

function App() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [showHubspotForm, setShowHubspotForm] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleJoinBeta = () => {
    setShowHubspotForm(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-orange-50 relative overflow-hidden">
      {showHubspotForm && <HubspotForm onClose={() => setShowHubspotForm(false)} />}
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <img 
                src="/agentmojo_logo_text.png" 
                alt="Agent Mojo" 
                className="h-8"
                style={{ height: 'calc(2rem * 1.2)' }}
              />
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-orange-600 transition-colors">Features</a>
              <a href="#about" className="text-gray-600 hover:text-orange-600 transition-colors">About</a>
              <button 
                onClick={handleJoinBeta}
                className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm font-semibold hover:shadow-lg transition-all transform hover:scale-105"
              >
                Join Beta
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute -top-[40%] -left-[20%] w-[70%] h-[70%] rounded-full bg-gradient-to-br from-orange-200/40 via-pink-200/30 to-transparent blur-3xl transition-all duration-1000 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`} />
        <div className={`absolute -bottom-[40%] -right-[20%] w-[70%] h-[70%] rounded-full bg-gradient-to-tl from-orange-200/40 via-yellow-200/30 to-transparent blur-3xl transition-all duration-1000 delay-300 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`} />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?q=80&w=3000&auto=format&fit=crop')] opacity-[0.03] mix-blend-overlay" />
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-4 h-4 bg-gradient-to-br from-orange-400 to-pink-400 rounded-full opacity-20 animate-float-${i + 1}`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.5}s`,
            }}
          />
        ))}
      </div>

      {/* Content Container */}
      <div className="relative pt-16">
        {/* Hero Section */}
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Supercharge your AI SDRs to consistently deliver{' '}
              <span className="relative inline-block">
                <span className="relative z-10 text-orange-600">
                  top-tier results
                  <Sparkles className="absolute -right-8 -top-6 w-6 h-6 text-yellow-400 animate-pulse" />
                </span>
                <div className="absolute inset-x-0 -bottom-1 h-3 bg-gradient-to-r from-orange-200/0 via-orange-200 to-orange-200/0 -z-10 transform skew-x-12 animate-pulse" />
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              with 24/7 QA simulations, and real-time conversation quality monitoring
            </p>
            <button 
              onClick={handleJoinBeta}
              className="bg-gradient-to-r from-orange-500 via-pink-500 to-orange-500 bg-[length:200%_100%] animate-gradient text-white text-lg font-semibold px-8 py-4 rounded-full transition-all transform hover:scale-105 hover:shadow-[0_0_20px_rgba(251,146,60,0.3)] shadow-lg"
            >
              🚀 Join Our Beta
            </button>
          </div>
        </div>

        {/* Main Features */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" id="features">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              QA & Monitoring for AI SDRs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ensure quality, compliance, and conversion efficiency with our AI SDR QA & monitoring platform
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Bot className="w-8 h-8 text-orange-600" />}
              title="AI SDR Quality Evaluation"
              description="Assess AI sales reps for clarity, accuracy, and effectiveness"
              delay={0}
            />
            <FeatureCard
              icon={<MessageSquareText className="w-8 h-8 text-orange-600" />}
              title="AI Persona Simulations"
              description="Test AI SDRs against realistic AI customer personas before deployment"
              delay={150}
            />
            <FeatureCard
              icon={<Shield className="w-8 h-8 text-orange-600" />}
              title="24/7 Real-time Monitoring"
              description="Get instant alerts on AI conversation quality and compliance for service downgrade"
              delay={300}
            />
          </div>
        </div>

        {/* Beta Section */}
        <div className="bg-gradient-to-b from-orange-50/50 to-orange-100/50 py-16 relative overflow-hidden" id="about">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200%] h-40 bg-gradient-to-b from-white to-transparent" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[200%] h-40 bg-gradient-to-t from-white to-transparent" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 md:p-12 hover:shadow-orange-200/50 transition-shadow duration-300">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  📢 Be among the first to define AI SDR standards!
                </h2>
                <p className="text-xl text-gray-600">
                  Be part of the next wave of AI-driven sales enablement.
                </p>
              </div>
              <div className="flex justify-center">
                <button 
                  onClick={handleJoinBeta}
                  className="group bg-gradient-to-r from-orange-500 via-pink-500 to-orange-500 bg-[length:200%_100%] animate-gradient text-white text-lg font-semibold px-8 py-4 rounded-full transition-all transform hover:scale-105 hover:shadow-[0_0_20px_rgba(251,146,60,0.3)] shadow-lg flex items-center gap-2"
                >
                  Join Our Beta
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <StatCard
              icon={<BarChart2 className="w-8 h-8 text-orange-600" />}
              number="24/7"
              label="Monitoring"
              delay={0}
            />
            <StatCard
              icon={<Bot className="w-8 h-8 text-orange-600" />}
              number="100+"
              label="AI Personas"
              delay={150}
            />
            <StatCard
              icon={<MessageSquareText className="w-8 h-8 text-orange-600" />}
              number="1000+"
              label="Conversations Analyzed"
              delay={300}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative bg-white/80 backdrop-blur-sm border-t border-gray-100 mt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-gray-600 text-center">
            © {new Date().getFullYear()} Agent Mojo. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

function FeatureCard({ icon, title, description, delay }: FeatureCardProps) {
  return (
    <div 
      className="group bg-white/80 backdrop-blur-sm p-6 rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-105 duration-300 animate-fade-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="mb-4 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">{icon}</div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  number: string;
  label: string;
  delay: number;
}

function StatCard({ icon, number, label, delay }: StatCardProps) {
  return (
    <div 
      className="group bg-gradient-to-br from-orange-50 to-orange-100/50 p-6 rounded-xl hover:shadow-lg transition-all hover:scale-105 duration-300 animate-fade-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex justify-center mb-4 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">{icon}</div>
      <div className="text-3xl font-bold text-gray-900 mb-1">{number}</div>
      <div className="text-gray-600">{label}</div>
    </div>
  );
}

export default App;